import { useId, useState, type ChangeEvent, type FormEvent } from 'react';
import Modal from '@tpct/mission-core/components/Modal';
import { toFileStoreError, type FileStoreApi } from '@tpct/mission-core/fileStore/fileStoreApi';
import type { LabelOptions, StoredFile } from '@tpct/mission-core/fileStore/fileStoreTypes';
import { __ } from '@/hub-shared/utils/i18n';

type CompanyFileEditDialogProps = {
  api: FileStoreApi;
  ownerId: number;
  file: StoredFile;
  types: LabelOptions['types'];
  onSaved: () => void;
  onClose: () => void;
};

const TYPE_LOCKED = 'type_locked_after_read';
const FIRST_YEAR = 2000;

const patchErrorMessage = (error: unknown): string => {
  const storeError = toFileStoreError(error);
  if (storeError.code === TYPE_LOCKED) {
    return __('Ce fichier a déjà été lu : son type ne peut plus changer. Supprimez puis redéposez le fichier.');
  }
  return storeError.message;
};

/** Relabels a stored file (type, year). A FEC/DSN already read keeps its type (ledger 44). */
const CompanyFileEditDialog = ({ api, ownerId, file, types, onSaved, onClose }: CompanyFileEditDialogProps) => {
  const typeId = useId();
  const yearId = useId();
  const [typeKey, setTypeKey] = useState(file.type_key);
  const [year, setYear] = useState(file.concerned_year === null ? '' : String(file.concerned_year));
  const [phase, setPhase] = useState<'idle' | 'saving'>('idle');
  const [error, setError] = useState<string | null>(null);

  const parsedYear = Number.parseInt(year, 10);
  const yearValid = Number.isInteger(parsedYear) && parsedYear >= FIRST_YEAR;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!yearValid) {
      return;
    }
    setPhase('saving');
    setError(null);
    try {
      await api.patch(ownerId, file.id, { type_key: typeKey, concerned_year: parsedYear });
      onSaved();
    } catch (patchError) {
      setError(patchErrorMessage(patchError));
      setPhase('idle');
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`${__('Modifier')} « ${file.name} »`} size="small">
      <form
        className="company-files-tab__edit"
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <label htmlFor={typeId}>{__('Type de fichier')}</label>
        <select
          id={typeId}
          value={typeKey}
          disabled={phase === 'saving'}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => setTypeKey(event.target.value)}
        >
          {types.map((type) => <option key={type.key} value={type.key}>{type.label}</option>)}
        </select>
        <label htmlFor={yearId}>{__('Année concernée')}</label>
        <input
          id={yearId}
          type="number"
          min={FIRST_YEAR}
          value={year}
          disabled={phase === 'saving'}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setYear(event.target.value)}
        />
        {error !== null && <p className="notice notice-error" role="alert">{error}</p>}
        <div className="form-actions">
          <button type="button" className="btn-secondary" disabled={phase === 'saving'} onClick={onClose}>
            {__('Annuler')}
          </button>
          <button type="submit" className="btn-primary" disabled={phase === 'saving' || !yearValid}>
            {phase === 'saving' ? __('Enregistrement…') : __('Enregistrer')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CompanyFileEditDialog;
